import { createElement } from '../../../../core/dom/helpers.js';
import { createOptionsProvider, subscribeWithInitial } from '../../../../core/options/options-provider.js';
import { createOptionsCache } from '../../../../core/options/options-cache.js';
import { renderOptionContent } from '../../../../core/options/render-option-content.js';
import { createPortalDropdown } from '../../../../core/dom/portal-dropdown.js';
import { renderChips } from '../../../option-picker/render-chips.js';
import { resolveChipLabelsFromOptions } from '../../../option-picker/resolve-chip-labels.js';

/**
 * Chips engine for multi-relation fields.
 * Renders chips + "Add..." trigger button + portal dropdown.
 *
 * @param {HTMLElement} container
 * @param {import('../../../../contracts/forms.js').FieldDriverContext} ctx
 * @returns {import('../../../../contracts/forms.js').FieldDriverHandle}
 */
export function mount(container, ctx) {
  const { def, onChange } = ctx;
  const mrDef = /** @type {import('../../../../contracts/forms.js').MultiRelationFieldDef} */ (def);
  if (typeof mrDef.loadOptions !== 'function') {
    throw new Error(`multi-relation field '${def.key}' requires loadOptions (or optionsUrl resolved by factory)`);
  }
  const loadOptionsFn = mrDef.loadOptions;

  const debounceMs = mrDef.debounceMs ?? 300;

  let destroyed = false;
  let forcedDisabled = false;
  /** @type {string[]} */
  let currentValues = Array.isArray(ctx.value) ? [.../** @type {string[]} */ (ctx.value)] : [];
  /** @type {import('../../../../contracts/forms.js').FieldOption[]} */
  let currentOptions = [];
  /** @type {boolean} */
  let isOpen = false;
  /** @type {number} */
  let highlightedIndex = -1;

  // --- DOM ---
  const wrapper = createElement('div', {
    class: 'multi-relation-driver',
    role: 'combobox',
    'aria-expanded': 'false',
    'aria-haspopup': 'listbox',
  });
  if (def.required) wrapper.setAttribute('aria-required', 'true');

  const chipsContainer = createElement('div', { class: 'multi-relation-driver__chips' });
  wrapper.appendChild(chipsContainer);

  const trigger = /** @type {HTMLButtonElement} */ (createElement('button', {
    type: 'button',
    class: 'multi-relation-driver__trigger',
  }));
  trigger.textContent = def.placeholder ?? 'Add...';

  if (def.disabled) {
    trigger.disabled = true;
  }

  // Dropdown panel (portal — appended to body)
  const panelId = `multi-relation-panel-${def.key}`;
  const panel = createElement('div', {
    class: 'multi-relation-driver__dropdown',
    role: 'listbox',
    id: panelId,
  });
  panel.style.display = 'none';

  const searchInput = /** @type {HTMLInputElement} */ (createElement('input', {
    type: 'text',
    class: 'multi-relation-driver__search',
    placeholder: 'Search...',
    role: 'searchbox',
    autocomplete: 'off',
  }));
  panel.appendChild(searchInput);

  const optionsList = createElement('ul', { class: 'multi-relation-driver__options' });
  panel.appendChild(optionsList);

  // Panel is body-appended on portal.open() (against anchor.ownerDocument),
  // not at construction.

  wrapper.appendChild(trigger);
  trigger.setAttribute('aria-controls', panelId);

  container.appendChild(wrapper);

  // --- Options provider (with TTL cache) ---
  const cache = createOptionsCache();
  const cacheNs = `multi-relation:${def.key}`;

  /** @type {import('../../../../contracts/forms.js').OptionLoader} */
  const cachedLoadOptions = async (opts) => {
    const cacheKey = JSON.stringify({ q: opts.query, v: opts.values ?? {} });
    if (cache.has(cacheNs, cacheKey)) {
      return /** @type {any} */ (cache.get(cacheNs, cacheKey));
    }
    const result = await loadOptionsFn(opts);
    cache.set(cacheNs, cacheKey, result);
    return result;
  };

  const provider = createOptionsProvider({
    loadOptions: cachedLoadOptions,
    mapOption: mrDef.mapOption,
    debounceMs,
  });

  // --- Portal lifecycle ---
  const portal = createPortalDropdown({
    anchor: wrapper,
    panel,
    onClose: () => closeDropdown(),
  });

  // --- Chip rendering ---
  /** @type {Map<string, string>} */
  const chipLabels = new Map();

  function doRenderChips() {
    renderChips(chipsContainer, {
      values: currentValues,
      labels: chipLabels,
      classes: { chip: 'multi-relation-driver__chip', removeBtn: 'multi-relation-driver__chip-remove' },
      onRemove: (id) => {
        const idx = currentValues.indexOf(id);
        if (idx === -1) return;
        currentValues = [...currentValues.slice(0, idx), ...currentValues.slice(idx + 1)];
        chipLabels.delete(id);
        doRenderChips();
        onChange(currentValues);
      },
      disabled: forcedDisabled || Boolean(def.disabled),
    });
  }

  function applyDisabled() {
    trigger.disabled = forcedDisabled || Boolean(def.disabled) || provider.getState().loading;
    // Re-render chips to update remove button states
    doRenderChips();
  }

  // Resolve labels for initial values
  for (const id of currentValues) {
    chipLabels.set(id, `#${id}`);
    if (mrDef.resolveLabel) {
      mrDef.resolveLabel(id).then((label) => {
        if (destroyed) return;
        chipLabels.set(id, label);
        doRenderChips();
      }).catch(() => { /* keep #id placeholder */ });
    }
  }

  const unsubscribe = subscribeWithInitial(provider, (state) => {
    if (destroyed) return;
    trigger.disabled = forcedDisabled || Boolean(def.disabled) || state.loading;
    currentOptions = state.options.slice();
    const { changed } = resolveChipLabelsFromOptions({
      values: currentValues,
      labels: chipLabels,
      options: currentOptions,
    });
    if (changed) doRenderChips();
    if (isOpen) {
      renderOptions();
    }
  });

  provider.load('', ctx.getFormValues?.() ?? {});

  // --- Dropdown ---
  function renderOptions() {
    optionsList.textContent = '';
    highlightedIndex = -1;
    currentOptions.forEach((opt, idx) => {
      const isSelected = currentValues.includes(opt.value);
      const li = createElement('li', {
        role: 'option',
        class: 'multi-relation-driver__option' + (isSelected ? ' multi-relation-driver__option--selected' : ''),
        'aria-selected': isSelected ? 'true' : 'false',
        id: `multi-relation-${def.key}-opt-${idx}`,
      });
      renderOptionContent(opt, li);
      li.addEventListener('mousedown', (e) => {
        e.preventDefault();
        if (!isSelected) {
          selectOption(opt);
        }
      });
      li.addEventListener('mouseover', () => {
        setHighlight(idx);
      });
      optionsList.appendChild(li);
    });
  }

  /** @param {number} idx */
  function setHighlight(idx) {
    const items = optionsList.querySelectorAll('.multi-relation-driver__option');
    items.forEach((item, i) => {
      item.classList.toggle('multi-relation-driver__option--highlighted', i === idx);
    });
    highlightedIndex = idx;
    if (idx >= 0 && items[idx]) {
      trigger.setAttribute('aria-activedescendant', items[idx].id || '');
    }
  }

  /** @param {import('../../../../contracts/forms.js').FieldOption} opt */
  function selectOption(opt) {
    if (currentValues.includes(opt.value)) return;
    currentValues = [...currentValues, opt.value];
    chipLabels.set(opt.value, opt.label);
    doRenderChips();
    closeDropdown();
    onChange(currentValues);
  }

  function openDropdown() {
    if (isOpen || trigger.disabled) return;
    isOpen = true;
    wrapper.setAttribute('aria-expanded', 'true');
    portal.open();
    searchInput.value = '';
    provider.load('', ctx.getFormValues?.() ?? {});
    renderOptions();
    setTimeout(() => searchInput.focus(), 0);
  }

  function closeDropdown() {
    if (!isOpen) return;
    isOpen = false;
    wrapper.setAttribute('aria-expanded', 'false');
    portal.close();
    trigger.removeAttribute('aria-activedescendant');
    trigger.focus();
  }

  // --- Events ---
  trigger.addEventListener('click', () => {
    if (isOpen) {
      closeDropdown();
    } else {
      openDropdown();
    }
  });

  searchInput.addEventListener('input', () => {
    provider.load(searchInput.value, ctx.getFormValues?.() ?? {});
  });

  /**
   * @param {KeyboardEvent} e
   */
  function handleKeydown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) openDropdown();
      else setHighlight(Math.min(highlightedIndex + 1, currentOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(Math.max(highlightedIndex - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (!isOpen) {
        openDropdown();
      } else if (highlightedIndex >= 0 && currentOptions[highlightedIndex]) {
        selectOption(currentOptions[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeDropdown();
    }
  }

  trigger.addEventListener('keydown', handleKeydown);
  searchInput.addEventListener('keydown', handleKeydown);

  // Render initial chips
  doRenderChips();

  return {
    setValue(value) {
      currentValues = Array.isArray(value) ? [.../** @type {string[]} */ (value)] : [];
      // Reset chip labels to placeholders, then resolve async
      chipLabels.clear();
      for (const id of currentValues) {
        // Try to find label in current options first
        const opt = currentOptions.find(o => o.value === id);
        if (opt) {
          chipLabels.set(id, opt.label);
        } else {
          chipLabels.set(id, `#${id}`);
          if (mrDef.resolveLabel) {
            mrDef.resolveLabel(id).then((label) => {
              if (destroyed) return;
              chipLabels.set(id, label);
              doRenderChips();
            }).catch(() => {});
          }
        }
      }
      doRenderChips();
    },
    getValue() {
      return currentValues;
    },
    setErrors() {
      // errors rendered by host
    },
    destroy() {
      destroyed = true;
      unsubscribe();
      provider.destroy();
      cache.destroy();
      closeDropdown();
      portal.destroy();
      wrapper.remove();
    },
    setDisabled(disabled) {
      forcedDisabled = disabled;
      applyDisabled();
    },
    setVisible(visible) {
      if (!visible && isOpen) closeDropdown();
    },
    clearValue() {
      if (currentValues.length === 0) return;
      currentValues = [];
      chipLabels.clear();
      doRenderChips();
      // No onChange — silent host-driven reset
    },
    getOptionsProvider() {
      return provider;
    },
    setAriaInvalid(invalid) {
      wrapper.setAttribute('aria-invalid', invalid ? 'true' : 'false');
    },
  };
}
